import { useCallback, useEffect, useRef, useState, type TouchEvent as ReactTouchEvent } from "react";
import { Link } from "react-router-dom";
import { featuredWorkIds, works, type Work } from "@/mocks/works";

const featured: Work[] = featuredWorkIds
  .map((id) => works.find((work) => work.id === id))
  .filter((work): work is Work => Boolean(work));

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback(
    (next: number) => {
      const total = featured.length;
      setIndex(((next % total) + total) % total);
    },
    [],
  );

  useEffect(() => {
    if (paused) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % featured.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [paused]);

  const onTouchStart = (event: ReactTouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const onTouchEnd = (event: ReactTouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  const active = featured[index];

  return (
    <section
      className="relative w-full h-[560px] md:h-[660px] overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {featured.map((work, i) => (
        <div
          key={work.id}
          className={[
            "absolute inset-0 transition-opacity duration-700",
            i === index ? "opacity-100" : "opacity-0 pointer-events-none",
          ].join(" ")}
        >
          <img
            src={work.stills[0]}
            alt={`${work.title} 剧照`}
            title={`${work.title} - 叶山小百合影视作品`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/25"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/30"></div>
        </div>
      ))}

      <div className="relative h-full px-4 md:px-8 flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          <div key={active.id} className="max-w-2xl animate-fade-up">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-accent-500 text-accent-950 text-xs font-medium">
                {active.type === "movie" ? "电影" : "电视剧"} · 焦点
              </span>
              <span className="text-xs md:text-sm text-background-50/70 tracking-widest">
                {active.year} · {active.genres.join(" / ")}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-background-50 leading-tight">
              《{active.title}》
            </h1>
            <p className="mt-4 text-base md:text-lg text-background-50/85 leading-relaxed max-w-xl">
              {active.tagline}
            </p>
            <p className="mt-2 text-sm text-background-50/60">{active.role}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/works/detail/${active.id}`}
                className="inline-flex items-center gap-2 px-6 h-11 rounded-md bg-accent-500 text-accent-950 text-sm font-medium hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-information-line"></i>
                </span>
                查看作品详情
              </Link>
              <Link
                to="/works"
                className="inline-flex items-center gap-2 px-6 h-11 rounded-md border border-background-50/40 text-background-50 text-sm font-medium hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-film-line"></i>
                </span>
                全部作品
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="上一个"
        className="hidden md:flex absolute left-4 lg:left-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border border-background-50/40 text-background-50 items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="下一个"
        className="hidden md:flex absolute right-4 lg:right-6 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full border border-background-50/40 text-background-50 items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {featured.map((work, i) => (
          <button
            key={work.id}
            type="button"
            onClick={() => go(i)}
            aria-label={`切换到 ${work.title}`}
            className={[
              "h-1.5 rounded-full transition-all cursor-pointer",
              i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70",
            ].join(" ")}
          ></button>
        ))}
      </div>
    </section>
  );
}